import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Eye,
  Image as ImageIcon,
  PackageOpen,
  Pencil,
  Plus,
  RefreshCw,
  Star,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import AdminPagination from "../components/AdminPagination";
import MediaPreviewModal from "../components/MediaPreviewModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { useAdmin } from "../context/AdminContext";
import { formatRupiah } from "../lib/format";
import { getMediaType } from "../lib/media";
import { stockBadge } from "../lib/storage";

const toneClass = {
  success: "bg-emerald-100 text-emerald-700 border-emerald-200",
  warning: "bg-amber-100 text-amber-700 border-amber-200",
  danger: "bg-rose-100 text-rose-700 border-rose-200",
};

const fallbackImage = "https://images.unsplash.com/photo-1608060563275-a60fb5027f8d";

const emptyForm = {
  id: "",
  name: "",
  category_id: "",
  short_description: "",
  description: "",
  benefits: "",
  how_to_use: "",
  price: "",
  cost_price: "",
  compare_price: "",
  stock: "",
  weight: "250",
  image: "",
  gallery: [],
  badge: "",
  is_active: true,
  is_featured: false,
};

const productFormSections = [
  { id: "informasi-dasar", label: "Informasi dasar" },
  { id: "detail-produk", label: "Detail produk" },
  { id: "info-penjualan", label: "Info penjualan" },
  { id: "gallery", label: "Gallery" },
];

const PAGE_SIZE = 10;

const AdminProductsPage = () => {
  const {
    products,
    categories,
    createProduct,
    updateProduct,
    deleteProduct,
    uploadProductImage,
    loading,
    error,
    refreshData,
  } = useAdmin();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [galleryUrl, setGalleryUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeSection, setActiveSection] = useState(productFormSections[0].id);
  const [page, setPage] = useState(1);
  const [previewMedia, setPreviewMedia] = useState(null);
  const dialogScrollRef = useRef(null);
  const sectionRefs = useRef({});

  const activeCategories = useMemo(
    () => categories.filter((category) => category.is_active !== false),
    [categories],
  );

  const filtered = useMemo(
    () => products.filter((product) =>
      product.name.toLowerCase().includes(search.toLowerCase()),
    ),
    [products, search],
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginatedProducts = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    if (open) {
      setActiveSection(productFormSections[0].id);
      requestAnimationFrame(() => {
        dialogScrollRef.current?.scrollTo({ top: 0 });
      });
    }
  }, [open]);

  const updateActiveSectionFromScroll = () => {
    const container = dialogScrollRef.current;
    if (!container) return;

    const containerTop = container.getBoundingClientRect().top;
    let currentSection = productFormSections[0].id;

    productFormSections.forEach((section) => {
      const element = sectionRefs.current[section.id];
      if (!element) return;

      const offsetTop = element.getBoundingClientRect().top - containerTop;
      if (offsetTop <= 180) {
        currentSection = section.id;
      }
    });

    setActiveSection(currentSection);
  };

  const scrollToFormSection = (sectionId) => {
    const section = sectionRefs.current[sectionId];
    if (!section) return;

    setActiveSection(sectionId);
    section.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openNew = () => {
    setForm({
      ...emptyForm,
      category_id: activeCategories[0]?.id ? String(activeCategories[0].id) : "",
    });
    setGalleryUrl("");
    setOpen(true);
  };

  const openEdit = (product) => {
    setForm({
      id: product.id,
      name: product.name || "",
      category_id: String(product.category_id || product.category?.id || ""),
      short_description: product.short_description || product.description || "",
      description: product.description || "",
      benefits: product.benefits || product.benefit || "",
      how_to_use: product.how_to_use || "",
      price: String(product.price ?? ""),
      cost_price: String(product.cost_price ?? ""),
      compare_price: product.compare_price ? String(product.compare_price) : "",
      stock: String(product.stock ?? ""),
      weight: product.weight ? String(product.weight) : "250",
      image: product.image || "",
      gallery: Array.isArray(product.gallery) ? product.gallery : [],
      badge: product.badge || "",
      is_active: product.is_active ?? product.active ?? true,
      is_featured: product.is_featured ?? false,
    });
    setGalleryUrl("");
    setOpen(true);
  };

  const addGalleryImage = (url) => {
    const cleaned = url.trim();
    if (!cleaned) return;
    setForm((current) => {
      const gallery = Array.from(new Set([...(current.gallery || []), cleaned])).slice(0, 8);
      return {
        ...current,
        gallery,
        image: current.image || cleaned,
      };
    });
    setGalleryUrl("");
  };

  const removeGalleryImage = (url) => {
    setForm((current) => {
      const gallery = (current.gallery || []).filter((item) => item !== url);
      return {
        ...current,
        gallery,
        image: current.image === url ? gallery[0] || "" : current.image,
      };
    });
  };

  const handleUpload = async (event, target = "gallery") => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      setUploading(true);
      const result = await uploadProductImage(file);
      if (target === "main") {
        setField("image", result.url);
        addGalleryImage(result.url);
      } else {
        addGalleryImage(result.url);
      }
      toast.success("Gambar berhasil diupload");
    } catch (uploadError) {
      toast.error(uploadError.message || "Gagal upload gambar");
    } finally {
      setUploading(false);
    }
  };

  const onSave = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return toast.error("Nama produk wajib diisi");
    if (!form.category_id) return toast.error("Kategori wajib dipilih");
    if (!form.short_description.trim()) return toast.error("Deskripsi singkat wajib diisi");
    if (!form.description.trim()) return toast.error("Detail produk wajib diisi");
    if (!form.price || Number(form.price) <= 0) return toast.error("Harga harus lebih dari 0");
    if (form.cost_price !== "" && Number(form.cost_price) < 0) return toast.error("Harga modal tidak boleh negatif");
    if (form.compare_price && Number(form.compare_price) <= Number(form.price)) {
      return toast.error("Harga coret harus lebih besar dari harga jual");
    }
    if (form.stock === "" || Number(form.stock) < 0) return toast.error("Stok tidak boleh negatif");

    const gallery = Array.from(new Set([form.image, ...(form.gallery || [])].filter(Boolean))).slice(0, 8);
    const payload = {
      category_id: Number(form.category_id),
      name: form.name.trim(),
      short_description: form.short_description.trim(),
      description: form.description.trim(),
      benefits: form.benefits.trim() || null,
      how_to_use: form.how_to_use.trim() || null,
      price: Number(form.price),
      cost_price: form.cost_price === "" ? 0 : Number(form.cost_price),
      compare_price: form.compare_price ? Number(form.compare_price) : null,
      stock: Number(form.stock),
      weight: form.weight ? Number(form.weight) : null,
      image: form.image || gallery[0] || fallbackImage,
      gallery,
      badge: form.badge.trim() || null,
      is_active: form.is_active,
      is_featured: form.is_featured,
    };

    try {
      setSaving(true);
      if (form.id) {
        await updateProduct(form.id, payload);
        toast.success("Produk berhasil diperbarui");
      } else {
        await createProduct(payload);
        toast.success("Produk berhasil ditambahkan");
      }
      setOpen(false);
      setForm(emptyForm);
    } catch (saveError) {
      toast.error(saveError.message || "Gagal menyimpan produk");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteProduct(id);
      toast.success("Produk berhasil dihapus");
    } catch (deleteError) {
      toast.error(deleteError.message || "Gagal menghapus produk");
    }
  };

  const openMediaPreview = (url, title = "Media produk") => {
    if (!url) return;
    setPreviewMedia({
      url,
      title,
      type: getMediaType(url),
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-1/3 rounded bg-stone-200" />
            <div className="h-6 w-1/2 rounded bg-stone-200" />
            <div className="rounded-2xl border border-stone-200 bg-white p-6">
              {[1, 2, 3].map((item) => (
                <div key={item} className="mb-4 h-16 rounded bg-stone-100" />
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-red-800">Gagal memuat produk</h3>
                <p className="mt-1 text-sm text-red-600">{error}</p>
              </div>
              <Button variant="outline" onClick={refreshData}>
                <RefreshCw className="mr-2 h-4 w-4" /> Retry
              </Button>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8" data-testid="admin-products-page">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-semibold text-stone-900 sm:text-4xl">
              Kelola Produk
            </h1>
            <p className="mt-2 text-lg text-stone-600">
              Atur produk, gallery, harga coret, dan detail publik.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input
              placeholder="Cari produk..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-12 rounded-xl border-stone-200 sm:w-64"
              data-testid="admin-product-search"
            />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={openNew}
                  className="h-12 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
                  data-testid="add-product-button"
                  disabled={activeCategories.length === 0}
                >
                  <Plus className="mr-2 h-5 w-5" /> Tambah Produk
                </Button>
              </DialogTrigger>
              <DialogContent
                ref={dialogScrollRef}
                onScroll={updateActiveSectionFromScroll}
                className="max-h-[92vh] max-w-5xl scroll-smooth overflow-y-auto"
              >
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">
                    {form.id ? "Edit Produk" : "Tambah Produk"}
                  </DialogTitle>
                </DialogHeader>
                {activeCategories.length === 0 ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800">
                    Tambahkan kategori aktif terlebih dahulu sebelum membuat produk.
                  </div>
                ) : (
                  <form onSubmit={onSave} className="grid gap-6 pt-2 lg:grid-cols-[240px_1fr]">
                    <aside className="sticky top-0 z-20 rounded-2xl border border-stone-200 bg-white/95 p-3 shadow-sm backdrop-blur lg:top-4 lg:self-start">
                      <div className="grid gap-2 sm:grid-cols-4 lg:grid-cols-1">
                      {productFormSections.map((item, index) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => scrollToFormSection(item.id)}
                          className={`group flex min-h-12 items-center justify-between gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold transition ${
                            activeSection === item.id
                              ? "bg-emerald-600 text-white shadow-sm shadow-emerald-100"
                              : "text-stone-600 hover:bg-stone-50 hover:text-stone-950"
                          }`}
                          aria-current={activeSection === item.id ? "step" : undefined}
                        >
                          <span className="flex items-center gap-3">
                            <span
                              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                                activeSection === item.id
                                  ? "bg-white text-emerald-700"
                                  : "bg-stone-100 text-stone-500 group-hover:bg-white"
                              }`}
                            >
                              {index + 1}
                            </span>
                            <span>{item.label}</span>
                          </span>
                          {activeSection === item.id && (
                            <span className="hidden rounded-full bg-white/20 px-2 py-1 text-[11px] font-semibold text-white xl:inline">
                              Aktif
                            </span>
                          )}
                        </button>
                      ))}
                      </div>
                    </aside>

                    <div className="space-y-6">
                      <section
                        id="informasi-dasar"
                        ref={(element) => {
                          sectionRefs.current["informasi-dasar"] = element;
                        }}
                        className="scroll-mt-6 rounded-2xl border border-stone-200 bg-white p-5"
                      >
                        <h3 className="text-lg font-semibold text-stone-900">Informasi dasar</h3>
                        <div className="mt-4 grid gap-4 sm:grid-cols-2">
                          <div className="sm:col-span-2">
                            <Label>Nama Produk</Label>
                            <Input value={form.name} maxLength={255} onChange={(event) => setField("name", event.target.value)} className="h-12 rounded-xl" />
                          </div>
                          <div>
                            <Label>Kategori Aktif</Label>
                            <Select value={form.category_id} onValueChange={(value) => setField("category_id", value)}>
                              <SelectTrigger className="h-12 rounded-xl">
                                <SelectValue placeholder="Pilih kategori" />
                              </SelectTrigger>
                              <SelectContent>
                                {activeCategories.map((category) => (
                                  <SelectItem key={category.id} value={String(category.id)}>
                                    {category.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Badge Produk</Label>
                            <Input value={form.badge} onChange={(event) => setField("badge", event.target.value)} placeholder="Best Seller, Diskon, Baru" className="h-12 rounded-xl" />
                          </div>
                          <div className="sm:col-span-2">
                            <Label>Deskripsi Singkat</Label>
                            <Textarea value={form.short_description} onChange={(event) => setField("short_description", event.target.value)} className="min-h-24 rounded-xl" />
                          </div>
                        </div>
                      </section>

                      <section
                        id="detail-produk"
                        ref={(element) => {
                          sectionRefs.current["detail-produk"] = element;
                        }}
                        className="scroll-mt-6 rounded-2xl border border-stone-200 bg-white p-5"
                      >
                        <h3 className="text-lg font-semibold text-stone-900">Detail produk</h3>
                        <div className="mt-4 space-y-4">
                          <div>
                            <Label>Detail Produk</Label>
                            <Textarea value={form.description} onChange={(event) => setField("description", event.target.value)} className="min-h-32 rounded-xl" />
                          </div>
                          <div>
                            <Label>Manfaat</Label>
                            <Textarea value={form.benefits} onChange={(event) => setField("benefits", event.target.value)} className="min-h-24 rounded-xl" />
                          </div>
                          <div>
                            <Label>Cara Pakai / Penyajian</Label>
                            <Textarea value={form.how_to_use} onChange={(event) => setField("how_to_use", event.target.value)} className="min-h-24 rounded-xl" />
                          </div>
                        </div>
                      </section>

                      <section
                        id="info-penjualan"
                        ref={(element) => {
                          sectionRefs.current["info-penjualan"] = element;
                        }}
                        className="scroll-mt-6 rounded-2xl border border-stone-200 bg-white p-5"
                      >
                        <h3 className="text-lg font-semibold text-stone-900">Info penjualan</h3>
                        <div className="mt-4 grid gap-4 sm:grid-cols-2">
                          <div>
                            <Label>Harga Jual (Rp)</Label>
                            <Input type="number" value={form.price} onChange={(event) => setField("price", event.target.value)} className="h-12 rounded-xl" />
                          </div>
                          <div>
                            <Label>Harga Modal / Cost (Rp)</Label>
                            <Input type="number" value={form.cost_price} onChange={(event) => setField("cost_price", event.target.value)} placeholder="Opsional, default 0" className="h-12 rounded-xl" />
                          </div>
                          <div>
                            <Label>Harga Coret (Rp)</Label>
                            <Input type="number" value={form.compare_price} onChange={(event) => setField("compare_price", event.target.value)} placeholder="Opsional, harus lebih tinggi" className="h-12 rounded-xl" />
                          </div>
                          <div>
                            <Label>Stok</Label>
                            <Input type="number" value={form.stock} onChange={(event) => setField("stock", event.target.value)} className="h-12 rounded-xl" />
                          </div>
                          <div>
                            <Label>Berat (gram)</Label>
                            <Input type="number" value={form.weight} onChange={(event) => setField("weight", event.target.value)} className="h-12 rounded-xl" />
                          </div>
                          <div className="flex items-center justify-between rounded-xl border border-stone-200 bg-stone-50 p-4">
                            <div>
                              <p className="font-medium text-stone-900">Produk Aktif</p>
                              <p className="text-sm text-stone-500">Tampil di halaman pembeli.</p>
                            </div>
                            <Switch checked={form.is_active} onCheckedChange={(value) => setField("is_active", value)} />
                          </div>
                          <div className="flex items-center justify-between rounded-xl border border-stone-200 bg-stone-50 p-4">
                            <div>
                              <p className="font-medium text-stone-900">Produk Unggulan</p>
                              <p className="text-sm text-stone-500">Bisa dipakai untuk highlight.</p>
                            </div>
                            <Switch checked={form.is_featured} onCheckedChange={(value) => setField("is_featured", value)} />
                          </div>
                        </div>
                      </section>

                      <section
                        id="gallery"
                        ref={(element) => {
                          sectionRefs.current.gallery = element;
                        }}
                        className="scroll-mt-6 rounded-2xl border border-stone-200 bg-white p-5"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-lg font-semibold text-stone-900">Gallery produk</h3>
                            <p className="mt-1 text-sm text-stone-500">Pilih gambar utama dan tambahkan sampai 8 gambar.</p>
                          </div>
                          <label className="inline-flex h-10 cursor-pointer items-center rounded-xl border border-stone-200 px-4 text-sm font-medium text-stone-700 hover:bg-stone-50">
                            <Upload className="mr-2 h-4 w-4" /> Upload
                            <input type="file" accept="image/*" className="hidden" onChange={(event) => handleUpload(event, "gallery")} disabled={uploading} />
                          </label>
                        </div>
                        <div className="mt-4 flex gap-2">
                          <Input value={galleryUrl} onChange={(event) => setGalleryUrl(event.target.value)} placeholder="Paste URL gambar produk" className="h-12 rounded-xl" />
                          <Button type="button" variant="outline" onClick={() => addGalleryImage(galleryUrl)} className="h-12 rounded-xl">
                            Tambah
                          </Button>
                        </div>
                        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                          {[form.image, ...(form.gallery || []).filter((item) => item !== form.image)].filter(Boolean).map((url) => (
                            <div
                              key={url}
                              role="button"
                              tabIndex={0}
                              onClick={() => openMediaPreview(url, form.name || "Gallery produk")}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  openMediaPreview(url, form.name || "Gallery produk");
                                }
                              }}
                              className={`group relative aspect-square cursor-zoom-in overflow-hidden rounded-xl border ${form.image === url ? "border-emerald-500 ring-2 ring-emerald-100" : "border-stone-200"} bg-stone-100`}
                            >
                              {getMediaType(url) === "video" ? (
                                <video src={url} className="h-full w-full object-cover" muted playsInline preload="metadata" />
                              ) : (
                                <img src={url} alt="Gallery produk" className="h-full w-full object-cover" />
                              )}
                              <div className="absolute inset-x-2 bottom-2 z-10 flex gap-1 opacity-0 transition group-hover:opacity-100">
                                <Button type="button" size="sm" onClick={(event) => { event.stopPropagation(); setField("image", url); }} className="h-8 flex-1 bg-emerald-600 text-xs text-white hover:bg-emerald-700">
                                  Utama
                                </Button>
                                <Button type="button" size="icon" variant="outline" onClick={(event) => { event.stopPropagation(); removeGalleryImage(url); }} className="h-8 w-8 bg-white">
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                              {form.image === url && (
                                <span className="absolute left-2 top-2 rounded-full bg-emerald-600 px-2 py-1 text-xs font-medium text-white">
                                  Utama
                                </span>
                              )}
                            </div>
                          ))}
                          {Array.from({ length: Math.max(0, 5 - ([form.image, ...(form.gallery || [])].filter(Boolean).length)) }).map((_, index) => (
                            <label key={index} className="flex aspect-square cursor-pointer items-center justify-center rounded-xl border border-dashed border-stone-300 bg-stone-50 text-center text-sm text-stone-500 hover:bg-stone-100">
                              <span>
                                <Upload className="mx-auto mb-2 h-5 w-5" /> Upload
                              </span>
                              <input type="file" accept="image/*" className="hidden" onChange={(event) => handleUpload(event, index === 0 ? "main" : "gallery")} disabled={uploading} />
                            </label>
                          ))}
                        </div>
                      </section>

                      <DialogFooter className="sticky bottom-0 rounded-2xl border border-stone-200 bg-white/95 p-4 backdrop-blur">
                        <Button type="button" variant="outline" onClick={() => setOpen(false)} className="h-12 rounded-xl">
                          Batal
                        </Button>
                        <Button type="submit" disabled={saving || uploading} className="h-12 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700" data-testid="save-product-button">
                          Simpan Produk
                        </Button>
                      </DialogFooter>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="hidden overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm md:block">
          <table className="w-full text-left">
            <thead className="bg-stone-50 text-sm text-stone-500">
              <tr>
                <th className="px-5 py-4 font-medium">Produk</th>
                <th className="px-5 py-4 font-medium">Kategori</th>
                <th className="px-5 py-4 font-medium">Harga</th>
                <th className="px-5 py-4 font-medium">Cost</th>
                <th className="px-5 py-4 font-medium">Stok</th>
                <th className="px-5 py-4 font-medium">Status</th>
                <th className="px-5 py-4 text-right font-medium">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center text-stone-500">
                    <PackageOpen className="mx-auto mb-2 h-12 w-12 text-stone-300" />
                    Belum ada produk.
                  </td>
                </tr>
              ) : (
                paginatedProducts.map((product) => {
                  const badge = stockBadge(product.stock);
                  const categoryName = product.category?.name || product.category || "-";
                  const active = product.is_active ?? product.active ?? true;
                  return (
                    <tr key={product.id} data-testid={`row-product-${product.id}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => openMediaPreview(product.image, product.name)}
                            className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl bg-stone-100"
                            aria-label={`Preview ${product.name}`}
                          >
                            {product.image ? (
                              getMediaType(product.image) === "video" ? (
                                <video src={product.image} className="h-full w-full object-cover" muted playsInline preload="metadata" />
                              ) : (
                                <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
                              )
                            ) : (
                              <ImageIcon className="h-5 w-5 text-stone-400" />
                            )}
                          </button>
                          <div>
                            <p className="font-medium text-stone-900">{product.name}</p>
                            <p className="line-clamp-1 text-sm text-stone-500">{product.short_description || product.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-stone-700">{categoryName}</td>
                      <td className="px-5 py-4">
                        <p className="font-medium text-stone-900">{formatRupiah(product.price)}</p>
                        {product.compare_price && (
                          <p className="text-sm text-stone-400 line-through">{formatRupiah(product.compare_price)}</p>
                        )}
                      </td>
                      <td className="px-5 py-4 text-stone-700">{formatRupiah(product.cost_price || 0)}</td>
                      <td className="px-5 py-4">
                        <span className={`rounded-full border px-3 py-1 text-xs font-medium ${toneClass[badge.tone]}`}>
                          {product.stock} - {badge.label}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-col gap-1">
                          <span className={`w-fit rounded-full px-3 py-1 text-xs font-medium ${active ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
                            {active ? "Aktif" : "Nonaktif"}
                          </span>
                          {product.is_featured && (
                            <span className="inline-flex w-fit items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700">
                              <Star className="mr-1 h-3 w-3" /> Unggulan
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex gap-2">
                          <Button asChild size="sm" variant="outline" className="h-10 rounded-lg">
                            <Link to={`/produk/${product.slug}`} target="_blank">
                              <Eye className="mr-1 h-4 w-4" /> Lihat
                            </Link>
                          </Button>
                          <Button size="sm" variant="outline" className="h-10 rounded-lg" onClick={() => openEdit(product)} data-testid={`edit-product-${product.id}`}>
                            <Pencil className="mr-1 h-4 w-4" /> Edit
                          </Button>
                          <DeleteDialog name={product.name} onConfirm={() => onDelete(product.id)} testId={`delete-product-${product.id}`} />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-4 md:hidden">
          {paginatedProducts.map((product) => {
            const badge = stockBadge(product.stock);
            const categoryName = product.category?.name || product.category || "-";
            const active = product.is_active ?? product.active ?? true;
            return (
              <div key={product.id} className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm" data-testid={`card-product-${product.id}`}>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => openMediaPreview(product.image, product.name)}
                    className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-stone-100"
                    aria-label={`Preview ${product.name}`}
                  >
                    {product.image ? (
                      getMediaType(product.image) === "video" ? (
                        <video src={product.image} className="h-full w-full object-cover" muted playsInline preload="metadata" />
                      ) : (
                        <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
                      )
                    ) : (
                      <ImageIcon className="m-auto h-5 w-5 text-stone-400" />
                    )}
                  </button>
                  <div className="flex-1">
                    <p className="font-display text-lg font-semibold text-stone-900">{product.name}</p>
                    <p className="text-sm text-stone-500">{categoryName}</p>
                    <p className="mt-1 font-medium text-stone-900">{formatRupiah(product.price)}</p>
                    <p className="text-sm text-stone-500">Cost: {formatRupiah(product.cost_price || 0)}</p>
                    {product.compare_price && (
                      <p className="text-sm text-stone-400 line-through">{formatRupiah(product.compare_price)}</p>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className={`rounded-full border px-3 py-1 text-xs font-medium ${toneClass[badge.tone]}`}>
                    {product.stock} - {badge.label}
                  </span>
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${active ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
                    {active ? "Aktif" : "Nonaktif"}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <Button asChild variant="outline" className="h-11 rounded-xl">
                    <Link to={`/produk/${product.slug}`} target="_blank">
                      <Eye className="mr-2 h-4 w-4" /> Lihat
                    </Link>
                  </Button>
                  <Button variant="outline" className="h-11 rounded-xl" onClick={() => openEdit(product)}>
                    <Pencil className="mr-2 h-4 w-4" /> Edit
                  </Button>
                  <DeleteDialog name={product.name} onConfirm={() => onDelete(product.id)} testId={`delete-product-mobile-${product.id}`} />
                </div>
              </div>
            );
          })}
        </div>

        <AdminPagination
          page={page}
          totalItems={filtered.length}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          itemLabel="produk"
        />
        {previewMedia && (
          <MediaPreviewModal media={previewMedia} onClose={() => setPreviewMedia(null)} />
        )}
      </main>
    </AdminLayout>
  );
};

const DeleteDialog = ({ name, onConfirm, testId }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button
        variant="outline"
        className="h-11 rounded-xl border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
        data-testid={testId}
      >
        <Trash2 className="mr-2 h-4 w-4" /> Hapus
      </Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Hapus {name}?</AlertDialogTitle>
        <AlertDialogDescription>Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Batal</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-rose-600 hover:bg-rose-700">
          Ya, Hapus
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default AdminProductsPage;
